
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Eye, EyeOff, PlusCircle, ExternalLink } from 'lucide-react';
import * as htmlToImage from 'html-to-image';
import DungeonCanvas from '../components/DungeonCanvas';
import Sidebar from '../components/Sidebar';
import RoomModal from '../components/RoomModal';
import SegmentModal from '../components/SegmentModal';
import HelpModal from '../components/HelpModal';
import { Dungeon, Room, GeneratorConfig, EncounterType, DungeonTheme, CustomEncounter } from '../types';
import { generateProceduralDungeon } from '../utils/dungeonGenerator';
import { useDungeonSync } from '../hooks/useDungeonSync';

const STORAGE_KEY = 'dungeon_weaver_v5_master';

const DEFAULT_ICONS = {
  rooms: { START: '🚩', BATTLE: '⚔️', TREASURE: '💰', CURIO: '🕯️', CAMP: '🔥', BOSS: '💀', EMPTY: '🔘' },
  encounters: { BATTLE: '⚔️', TRAP: '⚠️', TREASURE: '💎', OBSTACLE: '🧱', NONE: '' }
};

const DEFAULT_THEME: DungeonTheme = {
  primary: '#f43f5e',
  bg: '#050505',
  roomBg: '#111111',
  corridor: '#1c1c1c',
  text: '#e7e5e4',
  masterModeOpacity: 0.35,
  roomIcons: { ...DEFAULT_ICONS.rooms },
  encounterIcons: { ...DEFAULT_ICONS.encounters }
};

const TEMPLATES: Record<string, DungeonTheme> = {
  'Darkest': DEFAULT_THEME,
  'Toxic': { ...DEFAULT_THEME, primary: '#22c55e', bg: '#050a05', roomBg: '#101a10', corridor: '#152515' },
  'Frozen': { ...DEFAULT_THEME, primary: '#38bdf8', bg: '#020810', roomBg: '#0a1a2f', corridor: '#0f253f' },
  'Ethereal': { ...DEFAULT_THEME, primary: '#a855f7', bg: '#050510', roomBg: '#101025', corridor: '#151530' }
};

const MasterView: React.FC = () => {
  const [dungeon, setDungeon] = useState<Dungeon | null>(null);
  const [config, setConfig] = useState<GeneratorConfig>({ complexity: 12, branching: 2 });
  const [theme, setTheme] = useState<DungeonTheme>(DEFAULT_THEME);
  const [isMasterMode, setIsMasterMode] = useState(true);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string>(() => {
    return localStorage.getItem('dungeon_weaver_session_id') || Math.random().toString(36).substring(2, 8).toUpperCase();
  });
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [selectedSegment, setSelectedSegment] = useState<{ connId: string, segId: string } | null>(null);
  const [hostilities, setHostilities] = useState<string[]>(['Esqueleto', 'Culti', 'Aranha', 'Wraith']);
  const [customEncounters, setCustomEncounters] = useState<CustomEncounter[]>([]);
  const [myMaps, setMyMaps] = useState<Dungeon[]>([]);
  const [isExporting, setIsExporting] = useState(false);

  const { broadcastDungeon } = useDungeonSync(undefined, sessionId);

  useEffect(() => {
    localStorage.setItem('dungeon_weaver_session_id', sessionId);
  }, [sessionId]);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      setMyMaps(parsed);
      if (parsed.length > 0) {
        const lastDungeon = parsed[0];
        setDungeon(lastDungeon);
        setTheme(lastDungeon.theme || DEFAULT_THEME);
      }
    }
  }, []);

  useEffect(() => {
    if (dungeon && JSON.stringify(dungeon.theme) !== JSON.stringify(theme)) {
      setDungeon(prev => prev ? { ...prev, theme } : null);
    }
  }, [theme]);

  useEffect(() => {
    if (dungeon) {
      broadcastDungeon(dungeon);
      const updatedHistory = [dungeon, ...myMaps.filter(m => m.id !== dungeon.id)].slice(0, 15);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedHistory));
      localStorage.setItem('dungeon_weaver_active_state', JSON.stringify(dungeon));
    }
  }, [dungeon, broadcastDungeon]);

  const handleGenerate = () => {
    const { rooms, connections } = generateProceduralDungeon(config, theme);
    const newDungeon: Dungeon = {
      id: crypto.randomUUID(),
      name: `Masmorra #${Math.floor(Math.random() * 900) + 100}`,
      complexity: config.complexity,
      branching: config.branching,
      rooms,
      connections,
      createdAt: Date.now(),
      theme
    };
    setDungeon(newDungeon);
    setMyMaps(prev => [newDungeon, ...prev.filter(m => m.id !== newDungeon.id)].slice(0, 15));
  };

  const handleExportPNG = async () => {
    const node = document.getElementById('dungeon-inner-canvas');
    if (!node || isExporting) return;

    setIsExporting(true);
    try {
      const dataUrl = await htmlToImage.toPng(node, {
        backgroundColor: theme.bg,
        quality: 1.0,
        pixelRatio: 2
      });
      const link = document.createElement('a');
      link.download = `${dungeon?.name || 'masmorra'}.png`;
      link.href = dataUrl;
      link.click();
    } catch (error) {
      console.error('Falha na exportação:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleOpenPlayer = () => {
    window.open(`/#/player?s=${sessionId}`, '_blank');
  };

  const handleCopyVTTLink = () => {
    const vttUrl = `${window.location.origin}/#/vtt?s=${sessionId}`;
    navigator.clipboard.writeText(vttUrl);
    alert('Link do Foundry VTT copiado! Cole na configuração do seu módulo ou no campo de URL do Iframe.');
  };

  const handleRoomDelete = useCallback((roomId: string) => {
    console.log('Deletando sala:', roomId);
    setDungeon(prev => {
      if (!prev) return null;
      const filteredRooms = prev.rooms.filter(r => r.id !== roomId);
      const filteredConns = prev.connections.filter(c => c.fromId !== roomId && c.toId !== roomId);
      return { ...prev, rooms: filteredRooms, connections: filteredConns };
    });
    setSelectedRoomId(null);
  }, []);

  const handleDeleteConnection = useCallback((connId: string) => {
    setDungeon(prev => {
      if (!prev) return null;
      return {
        ...prev,
        connections: prev.connections.filter(c => `${c.fromId}-${c.toId}` !== connId)
      };
    });
    setSelectedSegment(null);
  }, []);

  const handleDeleteSegment = useCallback((connId: string, segId: string) => {
    console.log('Deletando segmento:', segId, 'da conexão:', connId);
    setDungeon(prev => {
      if (!prev) return null;
      return {
        ...prev,
        connections: prev.connections.map(conn =>
          `${conn.fromId}-${conn.toId}` === connId
            ? { ...conn, segments: conn.segments.filter(s => s.id !== segId) }
            : conn
        ).filter(conn => conn.segments.length > 0)
      };
    });
    setSelectedSegment(null);
  }, []);

  const handleUpdateRoom = useCallback((roomId: string, updates: Partial<Room>) => {
    setDungeon(prev => {
      if (!prev) return null;
      return {
        ...prev,
        rooms: prev.rooms.map(r => r.id === roomId ? { ...r, ...updates } : r)
      };
    });
  }, []);

  const handleUpdateSegment = useCallback((connId: string, segId: string, updates: Partial<{ encounter: EncounterType, revealed: boolean }>) => {
    setDungeon(prev => {
      if (!prev) return null;
      return {
        ...prev,
        connections: prev.connections.map(conn =>
          `${conn.fromId}-${conn.toId}` === connId
            ? { ...conn, segments: conn.segments.map(s => s.id === segId ? { ...s, ...updates } : s) }
            : conn
        )
      };
    });
  }, []);

  return (
    <div className="flex h-screen w-full text-[#e7e5e4] overflow-hidden select-none font-inter" style={{ backgroundColor: theme.bg }}>
      <Sidebar
        dungeon={dungeon}
        config={config} setConfig={setConfig}
        theme={theme} setTheme={setTheme}
        templates={TEMPLATES}
        hostilities={hostilities} setHostilities={setHostilities}
        customEncounters={customEncounters} setCustomEncounters={setCustomEncounters}
        onGenerate={handleGenerate}
        onExportPNG={handleExportPNG}
        isMasterMode={isMasterMode} setIsMasterMode={setIsMasterMode}
        history={myMaps}
        onLoadMap={(d) => { setDungeon(d); setTheme(d.theme); }}
        onDeleteMap={(id) => { const u = myMaps.filter(m => m.id !== id); setMyMaps(u); localStorage.setItem(STORAGE_KEY, JSON.stringify(u)); }}
      />

      <main className="flex-1 relative flex flex-col">
        <header className="h-16 border-b flex items-center justify-between px-8 z-10 bg-black/95" style={{ borderColor: `${theme.primary}44` }}>
          <div className="flex items-center gap-6">
            <h1 className="font-cinzel text-lg font-bold tracking-[0.4em] uppercase" style={{ color: theme.primary }}>
              {dungeon?.name || 'Mestre da Masmorra'}
            </h1>
            <button
              onClick={handleOpenPlayer}
              className="flex items-center gap-2 px-4 py-2 text-[8px] font-bold uppercase tracking-widest bg-stone-900 border border-stone-800 hover:border-white transition-all text-stone-400 hover:text-white"
            >
              <ExternalLink size={12} /> Abrir Visão do Jogador
            </button>
            <button
              onClick={() => setIsHelpOpen(true)}
              className="relative group ml-4"
            >
              <div className="absolute inset-0 bg-white/20 blur-md rounded-full opacity-0 group-hover:opacity-100 transition-opacity animate-pulse" style={{ backgroundColor: theme.primary }}></div>
              <div className="relative px-4 py-1.5 border border-white/10 bg-black/50 text-[9px] font-bold uppercase tracking-widest text-stone-400 group-hover:text-white group-hover:border-white/50 transition-all rounded-full flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: theme.primary }}></span> Guia
              </div>
            </button>
            <button
              onClick={handleCopyVTTLink}
              className="relative group ml-4"
            >
              <div className="absolute inset-0 bg-blue-500/20 blur-md rounded-full opacity-0 group-hover:opacity-100 transition-opacity animate-pulse"></div>
              <div className="relative px-4 py-1.5 border border-blue-500/10 bg-black/50 text-[9px] font-bold uppercase tracking-widest text-blue-400 group-hover:text-white group-hover:border-blue-500/50 transition-all rounded-full flex items-center gap-2">
                <ExternalLink size={10} /> Sync VTT ({sessionId})
              </div>
            </button>
          </div>
          <button
            onClick={() => setIsMasterMode(!isMasterMode)}
            className={`flex items-center gap-3 px-6 py-2 text-[10px] font-cinzel font-bold border transition-all ${isMasterMode ? 'bg-red-950/20' : 'opacity-40'}`}
            style={{ color: isMasterMode ? theme.primary : '#888', borderColor: isMasterMode ? theme.primary : '#333' }}
          >
            {isMasterMode ? <Eye size={14} /> : <EyeOff size={14} />} {isMasterMode ? 'Modo Mestre' : 'Modo Jogador'}
          </button>
        </header>

        <div className="flex-1 relative overflow-hidden">
          {isExporting && (
            <div className="absolute inset-0 z-[100] bg-black/80 flex items-center justify-center">
              <p className="font-cinzel text-xl animate-pulse tracking-widest uppercase" style={{ color: theme.primary }}>Gerando Atlas...</p>
            </div>
          )}
          {dungeon ? (
            <DungeonCanvas
              dungeon={dungeon}
              setDungeon={setDungeon}
              isMasterMode={isMasterMode}
              onEditRoom={setSelectedRoomId}
              onEditSegment={(c, s) => setSelectedSegment({ connId: c, segId: s })}
              onToggleRoom={(id) => handleUpdateRoom(id, { revealed: !dungeon.rooms.find(r => r.id === id)?.revealed })}
              onToggleSegment={(c, s) => {
                const conn = dungeon.connections.find(conn => `${conn.fromId}-${conn.toId}` === c);
                const seg = conn?.segments.find(seg => seg.id === s);
                handleUpdateSegment(c, s, { revealed: !seg?.revealed });
              }}
              customEncounters={customEncounters}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full opacity-10 gap-8">
              <PlusCircle size={100} />
              <p className="font-cinzel text-xl tracking-[0.5em] uppercase">Iniciar Expedição</p>
            </div>
          )}
        </div>
      </main>

      {selectedRoomId && dungeon && (
        <RoomModal
          room={dungeon.rooms.find(r => r.id === selectedRoomId)!}
          onClose={() => setSelectedRoomId(null)}
          onUpdate={(u) => handleUpdateRoom(selectedRoomId, u)}
          onDelete={handleRoomDelete}
          hostilities={hostilities}
          theme={theme}
        />
      )}
      {selectedSegment && dungeon && (
        <SegmentModal
          connId={selectedSegment.connId}
          segId={selectedSegment.segId}
          currentSegment={(() => {
            const conn = dungeon.connections.find(c => `${c.fromId}-${c.toId}` === selectedSegment.connId);
            const seg = conn?.segments.find(s => s.id === selectedSegment.segId);
            return { encounter: seg?.encounter || 'NONE', revealed: seg?.revealed || false };
          })()}
          onClose={() => setSelectedSegment(null)}
          onUpdate={(u) => handleUpdateSegment(selectedSegment.connId, selectedSegment.segId, u)}
          onDelete={() => handleDeleteSegment(selectedSegment.connId, selectedSegment.segId)}
          customEncounters={customEncounters}
          theme={theme}
        />
      )}
      {isHelpOpen && <HelpModal onClose={() => setIsHelpOpen(false)} theme={theme} />}
    </div>
  );
};

export default MasterView;
